import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { trpc } from '@org/clients/school';
import {
  ANNOUNCEMENT_STYLE_CARDS,
  announcementButtonBase,
  announcementContainerStyle,
  useUserType,
} from '@org/ui';
import { ANIMATION_REGISTRY } from './animations/registry';
import { resolveAnnouncementStyle } from './announcements/announcement-style';
import { isImpersonating } from '../lib/impersonation';

// -- Local palette (mirrors journey.tsx's poster look) --
const P = {
  maxRed: '#D41920',
  cream: '#FFF8F5',
  ink: '#1A1018',
  white: '#FFFFFF',
};

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const GATE_STYLES = `
@keyframes announcementBackdropIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
/* Bottom chrome over full-bleed animations. On phones the button rides
   higher so it clears the thumb zone / home indicator and is easier to spot. */
.announcement-media-chrome {
  padding: 56px 16px 28px;
}
@media (max-width: 700px) {
  .announcement-media-chrome {
    padding-bottom: calc(88px + env(safe-area-inset-bottom, 0px));
  }
  /* Styles flagged sheetOnMobile pin the card to the bottom edge on phones. */
  .announcement-overlay--sheet {
    align-items: flex-end !important;
    padding: 0 !important;
  }
}
`;

export function AnnouncementGate() {
  const { t, i18n } = useTranslation('p2e');
  // Viewing as a student is read-only: an announcement could not be acknowledged.
  const [impersonating] = useState(isImpersonating);
  const { data: pending } = trpc.announcements.pending.useQuery(undefined, { enabled: !impersonating });
  const utils = trpc.useUtils();
  const { gender } = useUserType();

  // Locally closed announcement id — lets the modal close instantly without
  // waiting for the mutation; invalidation then surfaces the next one (if any).
  const [closedId, setClosedId] = useState<string | null>(null);
  const [mediaEnded, setMediaEnded] = useState(false);
  const watchedRef = useRef(0);
  const primaryRef = useRef<HTMLButtonElement>(null);

  const acknowledgeMutation = trpc.announcements.acknowledge.useMutation({
    onSettled: () => utils.announcements.pending.invalidate(),
  });

  const current = pending?.[0];
  const open = !!current && current.announcementId !== closedId;

  // Visual style: `?announcementStyle=` (previews) > the style chosen in the
  // admin for this announcement > the app default.
  const styleName = useMemo(
    () => resolveAnnouncementStyle(undefined, current?.style ?? null),
    [current?.style],
  );

  // Reset per-announcement media tracking when a new announcement surfaces.
  useEffect(() => {
    watchedRef.current = 0;
    setMediaEnded(false);
  }, [current?.announcementId]);

  // Focus the primary action when the modal opens, and again when a gated
  // animation finishes (the button only mounts once mediaEnded flips).
  useEffect(() => {
    if (open) primaryRef.current?.focus();
  }, [open, current?.announcementId, mediaEnded]);

  const acknowledge = (status: 'completed' | 'dismissed') => {
    if (!current) return;
    setClosedId(current.announcementId);
    // Fire-and-forget; the mutation is idempotent server-side.
    acknowledgeMutation.mutate({
      announcementId: current.announcementId,
      status,
      ...(current.mediaType === 'video' || current.mediaType === 'animation'
        ? { watchedSeconds: Math.round(watchedRef.current) }
        : {}),
    });
  };

  // Escape closes (as a dismissal) only when the announcement is skippable.
  useEffect(() => {
    if (!open || !current?.skippable) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') acknowledge('dismissed');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, current?.announcementId, current?.skippable]);

  if (!current || !open) return null;

  // Bundled animation lookup — mediaUrl carries a registry key for
  // mediaType 'animation'. Unknown keys gracefully fall back to the
  // plain title/body card (rendered as mediaType 'none').
  const AnimationComponent =
    current.mediaType === 'animation'
      ? ANIMATION_REGISTRY[current.mediaUrl ?? '']
      : undefined;

  const hasCta = !!current.ctaLabel && !!current.ctaTo;
  const isGated =
    !current.skippable &&
    (current.mediaType === 'video' || !!AnimationComponent);
  const primaryDisabled = isGated && !mediaEnded;
  const reduced = prefersReducedMotion();

  const trackTime = (video: HTMLVideoElement) => {
    watchedRef.current = Math.max(watchedRef.current, video.currentTime);
  };

  const handlePrimary = () => {
    acknowledge('completed');
    if (hasCta && current.ctaTo) {
      // ctaTo is arbitrary (internal path or external URL); a plain
      // navigation covers both without fighting the typed router.
      window.location.assign(current.ctaTo);
    }
  };

  // ── Full-bleed native animation stage ──────────────────────────────────
  // No poster card here: the animation owns the whole viewport and carries
  // its own narrative (no title overlay). Only the action chrome floats
  // above it — skip in the top-right (when skippable), primary action
  // bottom-center over a legibility scrim.
  if (AnimationComponent) {
    return (
      <div
        role="dialog"
        aria-modal="true"
        aria-label={current.title}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 9000,
          background: P.ink,
          animation: reduced ? undefined : 'announcementBackdropIn .25s ease both',
        }}
      >
        <style>{GATE_STYLES}</style>

        {/* Stage — the animation fills the viewport */}
        <div style={{ position: 'absolute', inset: 0 }}>
          <AnimationComponent
            key={current.announcementId}
            onComplete={() => setMediaEnded(true)}
            onProgress={(seconds) => {
              watchedRef.current = Math.max(watchedRef.current, seconds);
            }}
          />
        </div>

        {/* Skip — top-right corner, only when skippable (Escape also works) */}
        {current.skippable && (
          <button
            type="button"
            onClick={() => acknowledge('dismissed')}
            style={{
              ...announcementButtonBase,
              padding: '0 26px',
              position: 'absolute',
              top: 16,
              right: 16,
              zIndex: 1,
              minWidth: 44,
              background: 'rgba(26,16,24,0.55)',
              color: P.cream,
              border: '1px solid rgba(255,248,245,0.35)',
            }}
          >
            {t('announcements.skip')}
          </button>
        )}

        {/* Bottom chrome — gradient scrim for button legibility. Hidden while
            a gated animation is still playing so the scrim doesn't shade the
            animation's own "Continuar" prompts; fades in over the final frame. */}
        {!primaryDisabled && (
          <div
            className="announcement-media-chrome"
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 10,
              background:
                'linear-gradient(to top, rgba(26,16,24,0.85) 0%, rgba(26,16,24,0.45) 55%, rgba(26,16,24,0) 100%)',
              pointerEvents: 'none',
              animation: reduced ? undefined : 'announcementBackdropIn .3s ease both',
            }}
          >
            <button
              ref={primaryRef}
              type="button"
              onClick={handlePrimary}
              style={{
                ...announcementButtonBase,
                padding: '0 26px',
                pointerEvents: 'auto',
                background: P.maxRed,
                color: P.white,
                border: 'none',
              }}
            >
              {hasCta ? current.ctaLabel : t('announcements.understood')}
            </button>
          </div>
        )}
      </div>
    );
  }

  // ── Card styles (classic, issue cover, wall note, school letter, extra edition) ──
  // The card only lays things out; the media element is built here so
  // watch-tracking stays in one place.
  const { Card, sheetOnMobile } = ANNOUNCEMENT_STYLE_CARDS[styleName];
  const media =
    current.mediaType === 'video' && current.mediaUrl ? (
      <video
        key={current.announcementId}
        controls
        playsInline
        preload="metadata"
        poster={current.posterUrl ?? undefined}
        src={current.mediaUrl}
        onTimeUpdate={(event) => trackTime(event.currentTarget)}
        onPause={(event) => trackTime(event.currentTarget)}
        onEnded={(event) => {
          trackTime(event.currentTarget);
          setMediaEnded(true);
        }}
        style={{ display: 'block', width: '100%', maxHeight: '55vh', background: '#000', borderRadius: 2 }}
      />
    ) : current.mediaType === 'image' && current.mediaUrl ? (
      <img
        src={current.mediaUrl}
        alt={current.title}
        style={{ display: 'block', width: '100%', maxHeight: '55vh', objectFit: 'contain', borderRadius: 2 }}
      />
    ) : null;

  return (
    <div
      className={sheetOnMobile ? 'announcement-overlay announcement-overlay--sheet' : 'announcement-overlay'}
      onClick={current.skippable ? () => acknowledge('dismissed') : undefined}
      style={{
        ...announcementContainerStyle,
        position: 'fixed',
        inset: 0,
        zIndex: 9000,
        background: 'rgba(26,16,24,0.9)',
        backdropFilter: 'blur(5px)',
        WebkitBackdropFilter: 'blur(5px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        animation: reduced ? undefined : 'announcementBackdropIn .25s ease both',
      }}
    >
      <style>{GATE_STYLES}</style>
      <Card
        title={current.title}
        body={current.body ?? null}
        media={media}
        date={new Date(current.startsAt ?? current.createdAt ?? Date.now())}
        locale={i18n.language?.startsWith('pt') ? 'pt-BR' : 'en-US'}
        primaryLabel={hasCta && current.ctaLabel ? current.ctaLabel : t('announcements.understood')}
        primaryDisabled={primaryDisabled}
        primaryRef={primaryRef}
        onPrimary={handlePrimary}
        skippable={current.skippable}
        skipLabel={t('announcements.skip')}
        onSkip={() => acknowledge('dismissed')}
        gatedHint={primaryDisabled ? t('announcements.watchToContinue') : null}
        genderContext={gender === 'male' ? 'male' : undefined}
        reducedMotion={reduced}
      />
    </div>
  );
}
